/* ---------------------------------------------------------------------------
   Locations map

   Loaded only by the map content block, not from index.js: MapLibre is around
   800KB and has no business on pages without a map.

   Basemap is OpenFreeMap's "positron" style. It needs no API key and no
   account, which is the whole point — the previous Carto basemap started
   requiring a key and answered unauthenticated requests with HTTP 200 and a
   watermarked tile, so nothing failed loudly enough to be caught before it
   went live. Positron is the same design lineage as Carto's light_all, so the
   light basemap the block's CSS assumes still holds.

   Positron is vector, hence MapLibre. The bridge keeps everything else on the
   Leaflet API: markers, popups and the key filters are unchanged from the
   raster version.
--------------------------------------------------------------------------- */

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { maplibreGL } from '@maplibre/maplibre-gl-leaflet';

// Leaflet's default marker resolves its own images relative to the stylesheet,
// which survives a CDN copy but not a bundler: the URLs end up pointing at
// /dist/assets/marker-icon.png, which is never emitted, and every marker using
// the default icon renders as a broken image. Importing them makes Vite emit
// the files and rewrite these to the hashed paths.
//
// Only markers whose CMS category matches no key row use the default icon, so
// this is invisible until live data carries a category the key does not list.
import markerIconUrl from 'leaflet/dist/images/marker-icon.png';
import markerIconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadowUrl from 'leaflet/dist/images/marker-shadow.png';

// Icon.Default overrides _getIconUrl to PREPEND a guessed directory to each
// url, guessed from the background-image on .leaflet-default-icon-path. In a
// production build that guess yields '' — the css url is inlined as a data uri,
// which its marker-icon.png suffix check rejects — so the imports above survive
// intact. Under `npm run dev` the css keeps a real path, the guess resolves to
// /node_modules/leaflet/dist/images/, and it gets prepended to imports that are
// already absolute, giving a doubled 404 path. Dropping the override leaves
// Icon.prototype._getIconUrl, which returns the option verbatim in both modes.
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
    iconUrl: markerIconUrl,
    iconRetinaUrl: markerIconRetinaUrl,
    shadowUrl: markerShadowUrl,
});

const STYLE_URL = 'https://tiles.openfreemap.org/styles/positron';

// OpenFreeMap serves OpenStreetMap-derived tiles under ODbL, so this is a
// licence condition rather than a courtesy. It renders through Leaflet's own
// attribution control, which means attributionControl must stay enabled.
const ATTRIBUTION =
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &middot; ' +
    '<a href="https://openfreemap.org/">OpenFreeMap</a>';

const ICON_SIZE = 45;

// Every install gets its own pin, with no grouping to click open. That leaves
// the coincident rows: 198 of the 432 live rows share a coordinate with another
// row, up to five on one point, because a site's several VeloBoxes were all
// recorded at the one site centroid. Plotted literally, only the topmost of
// each stack is clickable and 113 pins are unreachable — so a stack is fanned
// out onto a small circle instead, which is the one place the map shows a pin a
// few metres from its recorded position. The radius grows with the stack so
// five pins are no more crowded than two, and stays inside the footprint of the
// kind of site that produces a stack at all: a school, a housing block.
//
// Sized against the zoom a search actually lands on rather than the maximum:
// the icons are 45px, which is about 52m of ground at zoom 17, so a two-pin
// stack needs roughly 30m of radius to clear itself there and a five-pin stack
// roughly 55m. Smaller than this and the fan is real but invisible.
const FAN_BASE_METRES = 15;
const FAN_STEP_METRES = 8;

const METRES_PER_DEGREE_LAT = 111320;

// Long enough to cross the gap between a pin and its popup without the popup
// vanishing on the way, short enough that sweeping across a dense area does not
// leave a trail of panels behind.
const HOVER_CLOSE_DELAY_MS = 260;

// Long enough that a cold third-party basemap is not called a failure, short
// enough that a genuinely dead one does not spin forever.
const BASEMAP_TIMEOUT_MS = 15000;

// postcodes.io is free, open, keyless and UK-only, which matches both the data
// and the project's open-data-only constraint. Full postcodes and outcodes are
// separate endpoints.
const POSTCODES_API = 'https://api.postcodes.io';

const FULL_POSTCODE = /^[a-z]{1,2}\d[a-z\d]?\s*\d[a-z]{2}$/i;
const OUTCODE = /^[a-z]{1,2}\d[a-z\d]?$/i;

const SEARCH_PIN_SIZE = 20;

const EARTH_RADIUS_KM = 6371;
const NEARBY_RADIUS_KM = 5;

// Titles and categories come from the CMS, so they are escaped before going
// into the popup markup.
const escape = (value) =>
    String(value ?? '').replace(
        /[&<>"']/g,
        (character) =>
            ({
                '&': '&amp;',
                '<': '&lt;',
                '>': '&gt;',
                '"': '&quot;',
                "'": '&#39;',
            })[character],
    );

const readConfig = (block) => {
    const payload = block.querySelector('[data-map-config]');

    if (!payload) {
        return null;
    }

    try {
        return JSON.parse(payload.textContent);
    } catch {
        return null;
    }
};

// Tukey fences over each axis. The opening view should frame where the
// locations actually are, and a single stray point — whether a genuine
// overseas install or a coordinate typo, and the source has both — otherwise
// pulls fitBounds out until the UK is a smudge. Everything still plots; this
// only decides what the map opens on, and it backs off to the full set if the
// fence would throw away a meaningful share of the markers.
const withoutOutliers = (points) => {
    if (points.length < 8) {
        return points;
    }

    const fence = (values) => {
        const sorted = [...values].sort((a, b) => a - b);
        const at = (q) => sorted[Math.floor((sorted.length - 1) * q)];
        const q1 = at(0.25);
        const q3 = at(0.75);
        const span = (q3 - q1) * 1.5;

        return [q1 - span, q3 + span];
    };

    const [minLat, maxLat] = fence(points.map((point) => point.lat));
    const [minLng, maxLng] = fence(points.map((point) => point.lng));

    const kept = points.filter(
        (point) =>
            point.lat >= minLat &&
            point.lat <= maxLat &&
            point.lng >= minLng &&
            point.lng <= maxLng,
    );

    return kept.length >= points.length * 0.9 ? kept : points;
};

const toRadians = (degrees) => (degrees * Math.PI) / 180;

const distanceKm = (from, to) => {
    const dLat = toRadians(to.lat - from.lat);
    const dLng = toRadians(to.lng - from.lng);
    const h =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRadians(from.lat)) *
            Math.cos(toRadians(to.lat)) *
            Math.sin(dLng / 2) ** 2;

    return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
};

// Groups are keyed on the coordinate itself: the source repeats a site's
// centroid verbatim rather than approximating it, so exact equality is what
// actually collides here, and rounding to six decimals (~0.1m) is only there to
// survive the float round-trip through JSON.
const fanOutCoincident = (points) => {
    const groups = new Map();

    points.forEach((point) => {
        const key = `${point.lat.toFixed(6)},${point.lng.toFixed(6)}`;
        const group = groups.get(key);

        if (group) {
            group.push(point);
        } else {
            groups.set(key, [point]);
        }
    });

    groups.forEach((group) => {
        if (group.length < 2) {
            return;
        }

        // Sorted so the ring is arranged the same way on every load. Left in
        // CMS order it would reshuffle whenever an entry is edited, and a pin
        // that moves between visits is worse than one that is a few metres out.
        const ordered = [...group].sort((a, b) => a.title.localeCompare(b.title));
        const radius = FAN_BASE_METRES + FAN_STEP_METRES * ordered.length;

        // Guarded against a coordinate at the pole, where a degree of longitude
        // is no distance at all and the displacement would run away.
        const metresPerDegreeLng =
            METRES_PER_DEGREE_LAT *
            Math.max(Math.cos(toRadians(ordered[0].lat)), 0.01);

        ordered.forEach((point, index) => {
            // Starting due east rather than due north: the icons are anchored
            // at their tip, so a pair split vertically still overlaps where a
            // pair split horizontally does not.
            const angle = (2 * Math.PI * index) / ordered.length;

            point.lat += (radius * Math.sin(angle)) / METRES_PER_DEGREE_LAT;
            point.lng += (radius * Math.cos(angle)) / metresPerDegreeLng;
        });
    });

    return points;
};

// Hover and click show the same panel; the click only makes it stay. An earlier
// build used a tooltip for hover and the popup for click, which gave the two
// states different content — the hover label carried the name and nothing else,
// so the address and the rental link were only reachable by clicking.
//
// Leaflet popups have no hover mode, so it is wired here. One closure holds the
// state for the whole map, because only one popup is open at a time and which
// marker pinned it has to outlive any single marker's handlers.
const createHoverPopups = () => {
    let pinned = null;
    let timer = null;

    const cancelClose = () => {
        window.clearTimeout(timer);
        timer = null;
    };

    // A delay rather than an immediate close: the popup is not a child of the
    // marker, so the pointer leaves the marker on its way into the panel. This
    // gap is what lets it arrive and cancel the close, and it is the only
    // reason the rental link inside the popup can be reached with a mouse.
    const closeSoon = (layer) => {
        cancelClose();

        timer = window.setTimeout(() => {
            if (pinned !== layer) {
                layer.closePopup();
            }
        }, HOVER_CLOSE_DELAY_MS);
    };

    return (layer) => {
        const open = () => {
            cancelClose();
            layer.openPopup();
        };

        // Stable references, because Leaflet reuses one element per popup and
        // addEventListener only deduplicates a handler it has seen before.
        const holdOpen = cancelClose;
        const releasePanel = () => closeSoon(layer);

        layer.on('mouseover', open);
        layer.on('mouseout', releasePanel);

        // bindPopup's own click handler toggles, so a click always arrives here
        // having just closed the popup the hover opened — which is why this
        // reopens rather than leaving it be. Leaflet's handler runs first, and
        // the popupclose it fires on the way clears `pinned` before this sets
        // it, so the order works out.
        layer.on('click', () => {
            cancelClose();
            pinned = layer;
            layer.openPopup();
        });

        layer.on('popupclose', () => {
            if (pinned === layer) {
                pinned = null;
            }
        });

        layer.on('popupopen', (event) => {
            const panel = event.popup.getElement();

            if (!panel) {
                return;
            }

            panel.addEventListener('mouseenter', holdOpen);
            panel.addEventListener('mouseleave', releasePanel);
        });

        // Focus is the pointerless equivalent of hover. Bound on add rather
        // than once, because Leaflet builds the icon element when the marker
        // joins the map and a key filter takes it off and puts it back.
        layer.on('add', () => {
            const element = layer.getElement();

            if (!element || element.dataset.hoverBound === 'true') {
                return;
            }

            element.dataset.hoverBound = 'true';
            element.addEventListener('focus', open);
            element.addEventListener('blur', (event) => {
                const panel = layer.getPopup()?.getElement();

                // Tabbing from the pin to the popup's own close button is not
                // leaving the popup, and closing under it would strand focus.
                if (
                    panel &&
                    event.relatedTarget &&
                    panel.contains(event.relatedTarget)
                ) {
                    return;
                }

                closeSoon(layer);
            });
        });
    };
};

const buildPopup = (marker, keyItem, rental, viewLabel) => {
    const rows = [];

    if (keyItem) {
        rows.push(`<p class="map-popup__category">${escape(keyItem.label)}</p>`);
    }

    rows.push(`<p class="map-popup__title">${escape(marker.title)}</p>`);

    const detail = marker.site || marker.postcode;

    if (detail) {
        rows.push(`<p class="map-popup__site">${escape(detail)}</p>`);
    }

    // A Website URL on the entry is the destination wherever one is filled in.
    // Without one, only the rental-scheme VeloBoxes have somewhere to go: they
    // fall back to the shared scheme page, and every other category shows no
    // link at all. The wording follows the category rather than the URL, so a
    // rentable box still reads as a rental offer when it points at its own
    // page. rel="noopener" because every destination is off this site.
    const rentable = Boolean(keyItem && keyItem.rentable);
    const href = marker.url || (rentable && rental ? rental.url : null);

    if (href) {
        const label = rentable
            ? (rental && rental.label) || 'Find out more'
            : viewLabel || 'View';

        rows.push(
            '<p class="map-popup__actions">' +
                `<a class="map-popup__link" href="${escape(href)}" target="_blank" rel="noopener">` +
                `${escape(label)}</a></p>`,
        );
    }

    return `<div class="map-popup">${rows.join('')}</div>`;
};

/* --- Basemap state -------------------------------------------------------
   The tiles are third party and can take several seconds cold. Without this
   the frame sits empty with pins floating on nothing, which reads as broken
   and is indistinguishable from a basemap that will never arrive.
-------------------------------------------------------------------------- */

const watchBasemap = (block, basemap) => {
    const overlay = block.querySelector('[data-map-loading]');

    if (!overlay) {
        return;
    }

    const text = overlay.querySelector('[data-map-loading-text]');
    let settled = false;

    const clear = () => {
        if (settled) {
            return;
        }

        settled = true;
        overlay.hidden = true;
    };

    const fail = (message) => {
        if (settled) {
            return;
        }

        settled = true;
        overlay.dataset.state = 'error';

        if (text) {
            text.textContent = message;
        }
    };

    const glMap = basemap.getMaplibreMap ? basemap.getMaplibreMap() : null;

    if (!glMap) {
        clear();
        return;
    }

    // Not `load`, which resolves seconds before the first tile is painted, and
    // not `idle`, which the Leaflet bridge never fires at all — it keeps the GL
    // map in a permanent sync loop, so the map is never idle by MapLibre's
    // definition. Polling areTilesLoaded() on render is the signal that
    // survives the bridge.
    const settleWhenPainted = () => {
        if (glMap.isStyleLoaded() && glMap.areTilesLoaded()) {
            glMap.off('render', settleWhenPainted);
            clear();
        }
    };

    glMap.on('render', settleWhenPainted);
    glMap.on('error', () =>
        fail('The map background could not be loaded, but the locations are still plotted.'),
    );

    window.setTimeout(() => {
        fail('The map background is taking longer than usual to load.');
    }, BASEMAP_TIMEOUT_MS);
};

/* --- Search ---------------------------------------------------------------
   Three ways in, in order of cost: a substring match against the locations
   already on the page, then postcodes.io for a postcode the data does not
   itself contain, then the browser's own geolocation.
-------------------------------------------------------------------------- */

const lookupPostcode = async (term) => {
    const isFull = FULL_POSTCODE.test(term);
    const isOutcode = OUTCODE.test(term);

    if (!isFull && !isOutcode) {
        return null;
    }

    const path = isFull
        ? `/postcodes/${encodeURIComponent(term.replace(/\s+/g, ''))}`
        : `/outcodes/${encodeURIComponent(term)}`;

    try {
        const response = await fetch(`${POSTCODES_API}${path}`);

        if (!response.ok) {
            return null;
        }

        const body = await response.json();
        const result = body && body.result;

        if (!result || typeof result.latitude !== 'number') {
            return null;
        }

        return { lat: result.latitude, lng: result.longitude };
    } catch {
        return null;
    }
};

const initSearch = (block, api) => {
    const { map, entries, visibleEntries, fitTo, resetView, reveal, labelFor } = api;

    const wrapper = block.querySelector('[data-map-search]');
    const form = block.querySelector('[data-map-search-form]');
    const input = block.querySelector('[data-map-search-input]');
    const status = block.querySelector('[data-map-search-status]');
    const locate = block.querySelector('[data-map-locate]');

    if (!wrapper || !form || !input) {
        return;
    }

    // Hidden in the markup so it never shows without the script behind it.
    wrapper.hidden = false;

    const say = (message) => {
        if (status) {
            status.textContent = message ?? '';
        }
    };

    // Not interactive, so it never sits between the pointer and a location pin
    // drawn underneath it, and never takes a tab stop.
    let searchPin = null;

    const clearSearchPin = () => {
        searchPin?.remove();
        searchPin = null;
    };

    const dropSearchPin = (point) => {
        searchPin = L.marker([point.lat, point.lng], {
            icon: L.divIcon({
                className: 'map-search-pin',
                iconSize: [SEARCH_PIN_SIZE, SEARCH_PIN_SIZE],
            }),
            interactive: false,
            keyboard: false,
            zIndexOffset: 1000,
        }).addTo(map);
    };

    const showNearest = (point, label, { pin = false } = {}) => {
        clearSearchPin();

        // Only what the key currently shows. Ranking over every entry would
        // announce a nearest location the user has filtered off the map.
        const pool = visibleEntries();

        if (!pool.length) {
            say('Every category is switched off. Turn one on in the map key to search.');
            return;
        }

        const ranked = pool
            .map((entry) => ({ entry, km: distanceKm(point, entry) }))
            .sort((a, b) => a.km - b.km);

        const nearest = ranked[0];
        const within = ranked.filter((item) => item.km <= NEARBY_RADIUS_KM);

        // Frame the nearby set where there is one, otherwise the closest few,
        // so a sparse part of the country still gets a useful view. A pinned
        // point joins the frame, or the nearest few could leave it off screen.
        const framed = (within.length ? within : ranked.slice(0, 5)).map((item) => item.entry);

        if (pin) {
            dropSearchPin(point);
            framed.push(point);
        }

        fitTo(framed);

        say(
            within.length
                ? `${within.length} location${within.length === 1 ? '' : 's'} within ${NEARBY_RADIUS_KM}km of ${label}.`
                : `Nearest location to ${label} is about ${Math.round(nearest.km)}km away.`,
        );

        reveal(nearest.entry);
    };

    form.addEventListener('submit', async (event) => {
        event.preventDefault();

        const term = input.value.trim();

        clearSearchPin();

        if (!term) {
            say('');
            resetView();
            return;
        }

        // Postcode-shaped input is a place, not a string to grep for. Left to
        // the substring pass an outcode like "M1" matches any record whose
        // text happens to contain "m1" — a Glasgow postcode, say — instead of
        // taking the user to Manchester.
        if (FULL_POSTCODE.test(term) || OUTCODE.test(term)) {
            say('Searching…');

            const point = await lookupPostcode(term);

            if (point) {
                showNearest(point, term.toUpperCase(), { pin: true });
                return;
            }
        }

        const needle = term.toLowerCase();
        const hits = entries.filter((entry) => entry.haystack.includes(needle));
        const onMap = new Set(visibleEntries());
        const matches = hits.filter((entry) => onMap.has(entry));

        if (matches.length) {
            fitTo(matches);
            say(`${matches.length} location${matches.length === 1 ? '' : 's'} matching “${term}”.`);

            if (matches.length === 1) {
                reveal(matches[0]);
            }

            return;
        }

        // Matches exist but the key is hiding them. Saying "nothing found"
        // here would be a lie, and silently switching the category back on
        // would undo a choice the user just made — so name the category and
        // let them decide.
        if (hits.length) {
            const hiddenIn = [...new Set(hits.map((entry) => labelFor(entry.category)))];

            say(
                `${hits.length} match${hits.length === 1 ? '' : 'es'} for “${term}” ` +
                    `${hits.length === 1 ? 'is' : 'are'} hidden. ` +
                    `Switch ${hiddenIn.join(' or ')} back on in the map key.`,
            );

            return;
        }

        say(`Nothing found for “${term}”. Try a postcode, a town or a client name.`);
    });

    // Geolocation needs a secure context, so only offer it where it can work.
    if (locate && window.isSecureContext && navigator.geolocation) {
        locate.hidden = false;

        locate.addEventListener('click', () => {
            say('Finding your location…');

            navigator.geolocation.getCurrentPosition(
                (position) =>
                    showNearest(
                        {
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                        },
                        'you',
                    ),
                () => say('Could not get your location. Try a postcode instead.'),
                { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
            );
        });
    }
};

/* --- Map ----------------------------------------------------------------- */

const buildMap = (block) => {
    const canvas = block.querySelector('[data-map-canvas]');
    const config = readConfig(block);

    if (!canvas || !config) {
        return;
    }

    const {
        keyItems = [],
        markers = [],
        rental = null,
        viewLabel = 'View',
    } = config;

    // Both bounds are set explicitly because the GL basemap is a single Leaflet
    // layer that declares no zoom range, which leaves the map's own getMaxZoom()
    // as Infinity. minZoom stops the user zooming out past a single world copy;
    // maxZoom keeps fitBounds and the zoom control inside the range the vector
    // style actually has tiles for.
    const map = L.map(canvas, {
        scrollWheelZoom: true,
        minZoom: 3,
        maxZoom: 19,
    }).setView([54.5, -4], 6);

    // customAttribution, not `attribution`: the bridge's getAttribution() reads
    // options.attributionControl.customAttribution and nothing else. Left unset
    // it derives a wordier credit from the style's sources, and only once the
    // GL map fires `load` — so a slow or failed basemap would leave an ODbL
    // credit missing. This way Leaflet renders it the moment the layer is added.
    const basemap = maplibreGL({
        style: STYLE_URL,
        attributionControl: { customAttribution: ATTRIBUTION },
    }).addTo(map);

    watchBasemap(block, basemap);

    const keyByCategory = new Map(keyItems.map((item) => [item.category, item]));

    const icons = new Map(
        keyItems.map((item) => [
            item.category,
            L.icon({
                iconUrl: `/assets/img/${item.icon}-mapicon.svg`,
                iconSize: [ICON_SIZE, ICON_SIZE],
                iconAnchor: [ICON_SIZE / 2, ICON_SIZE],
                popupAnchor: [0, -ICON_SIZE],
            }),
        ]),
    );

    // A flat group, not a cluster: every install is its own pin at every zoom,
    // so the map reads as a count of installations at a glance and nothing has
    // to be clicked open to get at a location.
    const pins = L.layerGroup().addTo(map);

    // Parsed and fanned before a single marker exists, because the fan changes
    // the coordinate each marker is created at.
    const plotted = fanOutCoincident(
        markers
            .map((marker) => ({
                marker,
                title: `${marker.title ?? ''}`,
                lat: parseFloat(marker.lat),
                lng: parseFloat(marker.lng),
            }))
            .filter(
                (point) => !Number.isNaN(point.lat) && !Number.isNaN(point.lng),
            ),
    );

    const attachHoverPopup = createHoverPopups();

    const entries = [];
    const counts = new Map();

    plotted.forEach(({ marker, lat, lng }) => {
        const keyItem = keyByCategory.get(marker.category) ?? null;
        const icon = icons.get(marker.category);

        const layer = icon ? L.marker([lat, lng], { icon }) : L.marker([lat, lng]);

        layer.bindPopup(buildPopup(marker, keyItem, rental, viewLabel));
        attachHoverPopup(layer);

        entries.push({
            layer,
            category: marker.category,
            lat,
            lng,
            haystack: [marker.title, marker.site, marker.postcode]
                .filter(Boolean)
                .join(' ')
                .toLowerCase(),
        });

        counts.set(marker.category, (counts.get(marker.category) ?? 0) + 1);
    });

    // Anything whose category matches no key row keeps the default marker and
    // stays on the map, since no key row governs it.
    const active = new Set(entries.map((entry) => entry.category));

    const visibleEntries = () => entries.filter((entry) => active.has(entry.category));

    const render = () => {
        pins.clearLayers();
        visibleEntries().forEach((entry) => pins.addLayer(entry.layer));
    };

    // The cap only bites when the bounds are already tiny — a search that lands
    // on one site, which is exactly the case where several installs share a
    // coordinate and have been fanned apart. At 15 the fan is still inside a
    // single icon and they read as one pin; 17 is the zoom the fan is sized
    // for. Nothing else gets close to the cap, so the opening view is unchanged.
    const fitTo = (list, options = {}) => {
        const bounds = L.latLngBounds(list.map((entry) => [entry.lat, entry.lng]));

        if (bounds.isValid()) {
            map.fitBounds(bounds, { padding: [40, 40], maxZoom: 17, ...options });
        }
    };

    const resetView = () => fitTo(withoutOutliers(visibleEntries()));

    // Both callers fit the map first, and fitBounds animates. A popup opened
    // mid-animation auto-pans against the fit still in flight, so the two fight
    // and the view lands somewhere neither asked for. moveend does not fire
    // when the fit was a no-op, hence the timeout as a floor.
    const afterSettle = (fn) => {
        let fired = false;
        const go = () => {
            if (!fired) {
                fired = true;
                fn();
            }
        };

        map.once('moveend', go);
        window.setTimeout(go, 700);
    };

    const reveal = (entry) => afterSettle(() => entry.layer.openPopup());

    const labelFor = (category) => keyByCategory.get(category)?.label ?? category;

    render();

    block.querySelectorAll('[data-map-count]').forEach((element) => {
        const total = counts.get(element.dataset.mapCount) ?? 0;
        element.textContent = total ? String(total) : '';
    });

    block.querySelectorAll('[data-map-filter]').forEach((button) => {
        button.addEventListener('click', () => {
            const category = button.dataset.mapFilter;
            const isOn = button.getAttribute('aria-pressed') === 'true';

            if (isOn) {
                active.delete(category);
            } else {
                active.add(category);
            }

            button.setAttribute('aria-pressed', String(!isOn));
            render();

            // Refitting to what is left is the point of filtering down; leaving
            // the view framed for the full set strands the user on empty space.
            const remaining = visibleEntries();

            if (remaining.length) {
                fitTo(remaining);
            }
        });
    });

    initSearch(block, { map, entries, visibleEntries, fitTo, resetView, reveal, labelFor });

    // Leaflet measures its container when the map is created. The frame is
    // sized by CSS, so measure again once layout has settled: fitBounds against
    // a container of unknown height resolves to the minimum zoom and drops the
    // map out to a view of the whole world.
    requestAnimationFrame(() => {
        map.invalidateSize();
        fitTo(withoutOutliers(entries));
    });
};

const init = () => document.querySelectorAll('[data-map-block]').forEach(buildMap);

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
